/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { savedTranscriptVersions } from "actions/AssetActions";
import * as ProjectUtilities from "utilities/ProjectUtilities";
import { mapArrayToSet, saveTranscript } from "utilities/ProjectUtilities";
import * as ActionTypes from "constants/ActionTypes";
import { mapFromServerFormat, mapToServerFormat } from "utilities/ApiUtilities";
import gatewayFetch from "utilities/gatewayFetch";
import { generateTranscriptPreview } from "utilities/BlockUtilities";
import { addToast } from "actions/ToastMessageActions";
import {
  LoadProjectAction,
  SaveProjectAction,
  SetStreamingAction,
  SyncTranscriptsAction,
} from "types/Actions";
import { RecordingSession } from "types/RecordingSession";
import { AppAction } from "types/AppAction";
import { setFilter } from "actions/FilterActions";
import FilterTypes, {
  Filter,
  selectEnhanceSpeechFilter,
} from "constants/FilterTypes";
import { ModelVersion } from "../components/EnhancedSpeech/useEnhanceSpeech/ModelVersion";

export function loadProject(projectJson: any): LoadProjectAction {
  const {
    tracks,
    manifest,
    recording_sessions: recordingSessionsJson,
    ...projectFields
  } = projectJson;
  const assets = ProjectUtilities.mapTracksToAssets(tracks);
  const clips = ProjectUtilities.mapBackendBlocksToClips(manifest.blocks);
  const recordingSessions = mapArrayToSet(
    mapFromServerFormat(recordingSessionsJson) as RecordingSession[]
  );
  const project = {
    ...projectFields,
    filters: Object.fromEntries(
      Object.entries(manifest.filters ?? {}).map(
        ([key, value]: [string, Record<string, any>]) => [
          key,
          mapFromServerFormat(value),
        ]
      )
    ),
  };

  return {
    type: ActionTypes.LOAD_PROJECT,
    project,
    assets,
    clips,
    recordingSessions,
  };
}

export function renameProject(name: string): AppAction<Promise<void>> {
  return async (dispatch, getState): Promise<void> => {
    const { project } = getState();
    await ProjectUtilities.setName({ project, name });
    dispatch({
      type: ActionTypes.SET_PROJECT_NAME,
      name,
    });
  };
}

export const syncTranscripts = (): AppAction<
  Promise<SyncTranscriptsAction>
> => {
  return async (dispatch, getState): Promise<SyncTranscriptsAction> => {
    const {
      assets: assetMap,
      project: { id: projectId },
    } = getState();
    const assets = Object.values(assetMap);

    const outOfSyncAssets = assets.filter(
      (asset) =>
        !!asset.transcript &&
        savedTranscriptVersions[asset.id] !== asset.transcriptVersion
    );

    const saveRequests = outOfSyncAssets.map(
      async (asset): Promise<Response> => {
        try {
          const response = await saveTranscript(
            projectId,
            asset.id,
            asset.transcript! // We know this is not null/undef due to the filter above
          );

          if (response.ok) {
            savedTranscriptVersions[asset.id] = asset.transcriptVersion;
          } else {
            const errorMessage = `saveTranscript failed with status: ${response.status}`;
            console.error(errorMessage);
            newrelic.noticeError(errorMessage);
          }

          return response;
        } catch (reason) {
          const errorMessage = `saveTranscript failed: ${reason}`;
          console.error(errorMessage);
          newrelic.noticeError(errorMessage);
          throw reason;
        }
      }
    );

    await Promise.all(saveRequests);

    return {
      type: ActionTypes.SYNC_TRANSCRIPTS,
    };
  };
};

export function saveProject(): AppAction<Promise<SaveProjectAction>> {
  return async (dispatch, getState): Promise<SaveProjectAction> => {
    const state = getState();
    const { project, clips } = state;

    const transcriptPreview = generateTranscriptPreview(state);
    const mappedClips = mapToServerFormat(clips);

    // TODO: Handle fetch failure?
    // TODO: Await fetch? We await this action in multiple places as if it's blocking...
    gatewayFetch(`/api/v1/projects/${project.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        manifest: {
          filters: Object.fromEntries(
            Object.entries(project.filters ?? {}).map(([key, value]) => [
              key,
              mapToServerFormat(value as Filter),
            ])
          ),
          blocks: mappedClips,
          transcript_preview: transcriptPreview,
        },
      }),
    });

    // TODO: Are we just returning this for testing? It isn't handled in any reducers.
    return {
      type: ActionTypes.SAVE,
    };
  };
}

export function enhanceProject(): AppAction<Promise<void>> {
  return async (dispatch, getState): Promise<void> => {
    const { project } = getState();

    try {
      gatewayFetch(`/api/v1/projects/${project.id}/enhance`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
    } catch (error) {
      console.error(error);
      newrelic.noticeError(error);
    }
  };
}

export const setEnhanceSpeechModelVersion = (
  enhanceSpeechModelVersion: ModelVersion
): AppAction<Promise<void>> => {
  return async (dispatch, getState): Promise<void> => {
    const { project } = getState();
    try {
      const response = await ProjectUtilities.setEnhanceSpeechModelVersion({
        project,
        enhanceSpeechModelVersion,
      });

      const responseJson = await response.json();

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }

      dispatch({
        type: ActionTypes.SET_ENHANCE_SPEECH_MODEL_VERSION,
        modelVersion: responseJson.enhance_speech_model_version,
      });
    } catch (error) {
      console.error(
        `Failed to set enhance speech model version: ${error.message}`
      );
      newrelic.noticeError(error);
      throw error;
    }
  };
};

export function shareProject(email: string): AppAction<Promise<boolean>> {
  return async (dispatch, getState): Promise<boolean> => {
    try {
      const { project } = getState();
      return await ProjectUtilities.shareProject({ project, email });
    } catch (e) {
      console.error(e);
      newrelic.noticeError(e);
      return false;
    }
  };
}

export function sendUninvite(email: string): AppAction<Promise<boolean>> {
  return async (dispatch, getState): Promise<boolean> => {
    const { project } = getState();
    await ProjectUtilities.uninvite({ project, email });
    return true;
  };
}

export function convertProjectToTemplate(): AppAction<Promise<void>> {
  return async (dispatch, getState): Promise<void> => {
    const { project } = getState();
    dispatch({
      type: ActionTypes.CONVERT_PROJECT_TEMPLATE_REQUEST,
    });

    await ProjectUtilities.convertProjectToTemplate({ project });
    dispatch({
      type: ActionTypes.CONVERT_PROJECT_TEMPLATE_SUCCESS,
    });
  };
}

export function deleteTemplate(): AppAction<Promise<void>> {
  return async (dispatch, getState): Promise<void> => {
    const { project } = getState();
    await ProjectUtilities.deleteAssociatedTemplate({ project });
    await dispatch(
      addToast({ message: I18n.t("report_abuse.delete_abusive_success") })
    );
  };
}

export const makeProjectPublic = (): AppAction<Promise<void>> => {
  return async (dispatch, getState): Promise<void> => {
    const { project } = getState();

    const anonymousAccessKey = await ProjectUtilities.createAnonymousAccessKey(
      project
    );

    dispatch({
      type: ActionTypes.SET_PROJECT_ANONYMOUS_ACCESS_KEY,
      anonymousAccessKey,
    });
  };
};

export const revokeProjectAnonymousAccess = (): AppAction<Promise<void>> => {
  return async (dispatch, getState): Promise<void> => {
    const { project } = getState();

    const key = project.anonymous_access_key;

    dispatch({
      type: ActionTypes.SET_PROJECT_ANONYMOUS_ACCESS_KEY,
      anonymousAccessKey: null,
    });

    try {
      await ProjectUtilities.revokeAnonymousAccessKey(project);
    } catch (e) {
      dispatch({
        type: ActionTypes.SET_PROJECT_ANONYMOUS_ACCESS_KEY,
        anonymousAccessKey: key,
      });
      throw e;
    }
  };
};

export const setEnhanceStrengthPercent = (
  enhanceStrengthPercent: number
): AppAction<Promise<void>> => {
  return async (dispatch, getState): Promise<void> => {
    const { project } = getState();
    const { enabled } = selectEnhanceSpeechFilter(project.filters);

    await dispatch(
      setFilter({
        filterName: FilterTypes.ENHANCE_SPEECH,
        enabled,
        strengthPercent: enhanceStrengthPercent,
      })
    );
  };
};
